import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

// @ts-ignore
import { fetchPteCoreSpeakingQuestions } from '../../api';
import {
    CheckCircle2
} from 'lucide-react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';

// Import service functions and the new UI component
import PteCoreSpeakingScore, {
    scoreReadAloudAttempt,
    scoreRepeatSentenceAttempt,
    scoreDescribeImageAttempt,
    scoreRespondToSituationAttempt,
    scoreAnswerShortQuestionAttempt,
    ScoreResult
} from './PteCoreSpeakingScore.tsx';

// Import both CSS files - reading CSS for consistent layout, speaking CSS for specific components
import './ptecorereading.css';
import './ptecorespeaking.css';

declare var process: any;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const getAssetUrl = (path: string) => {
    if (!path) return "";
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    // If it has a path separator, assume full path is provided (e.g. uploads/foo.mp3)
    if (cleanPath.includes("/")) return `${API_BASE_URL}/${cleanPath}`;
    // Otherwise assume it's a GridFS file served via /image/
    return `${API_BASE_URL}/image/${cleanPath}`;
};

// Constants
const TOTAL_SECONDS = 50 * 60; // 50 minutes for speaking section

// --- Types (Inlined as requested) ---

/* eslint-disable no-unused-vars */
export enum TestState {
    IDLE = 'idle',
    COUNTDOWN = 'countdown',
    LISTENING = 'listening',
    PREPARING = 'preparing',
    RECORDING = 'recording',
    ANALYZING = 'analyzing',
    COMPLETED = 'completed'
}
/* eslint-enable no-unused-vars */

export interface BaseQuestion {
    _id: string; // MongoDB ID
    id?: string; // Fallback
    questionTitle: string; // e.g. "Read Aloud"
    questionContent: string;
    preparationTime: number;
    recordingTime: number;
    component?: string;

    // Type specific optional fields
    audio?: string; // URL for audio
    img?: string; // URL for image

    // Legacy fields mapped from backend
    text?: string;
    audioText?: string;
    imageUrl?: string;
    imageDescription?: string;
    situationText?: string;
    questionText?: string;
    taskTitle?: string; // Added to fix lint error
}

export interface AudioVisualizerProps {
    stream: MediaStream | null;
    isRecording: boolean;
}



// --- Sub-Components ---

// --- Sub-Component: AudioVisualizer ---

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ stream, isRecording }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const requestRef = useRef<number | undefined>(undefined);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

    useEffect(() => {
        if (!stream || !isRecording || !canvasRef.current) return;

        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            audioContextRef.current = audioContext;

            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;

            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            sourceRef.current = source;

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            if (!ctx) return;

            const draw = () => {
                requestRef.current = requestAnimationFrame(draw);
                analyser.getByteFrequencyData(dataArray);

                ctx.fillStyle = '#f3f4f6'; // Light gray background matching CSS
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                const barWidth = (canvas.width / bufferLength) * 2.5;
                let barHeight;
                let x = 0;

                for (let i = 0; i < bufferLength; i++) {
                    barHeight = dataArray[i] / 2;

                    // PTE Blue gradient style
                    ctx.fillStyle = `rgb(${0}, ${127 + (barHeight)}, ${163})`;
                    ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

                    x += barWidth + 1;
                }
            };

            draw();
        } catch (err) {
            console.error("Error visualizing audio", err);
        }

        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, [stream, isRecording]);

    return (
        <div className="visualizer-container">
            {!isRecording && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs">
                    Volume
                </div>
            )}
            <canvas
                ref={canvasRef}
                width={300}
                height={64}
                className="w-full h-full"
            />
        </div>
    );
};

// Generic Props for Tasks
interface TaskProps {
    question: BaseQuestion;
    savedResult?: ScoreResult | null;
    onResultChange: (result: ScoreResult) => void;
}

const ReadAloudTask: React.FC<TaskProps> = ({ question, savedResult, onResultChange }) => {
    const [testState, setTestState] = useState<TestState>(TestState.PREPARING);
    const [timeLeft, setTimeLeft] = useState(question.preparationTime || 40);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [transcription, setTranscription] = useState('');

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recognitionRef = useRef<any>(null);
    const chunksRef = useRef<Blob[]>([]);

    useEffect(() => {
        if (savedResult) {
            setTestState(TestState.COMPLETED);
        } else {
            setTestState(TestState.PREPARING);
            setTimeLeft(question.preparationTime || 40);
        }
    }, [question._id, savedResult]);

    useEffect(() => {
        const initMic = async () => {
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setStream(audioStream);
            } catch (err) {
                console.error("Error accessing microphone:", err);
            }
        };
        initMic();
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    useEffect(() => {
        if (testState === TestState.PREPARING || testState === TestState.RECORDING) {
            if (timeLeft > 0) {
                const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                handleTimerExpiry();
            }
        }
    }, [testState, timeLeft]);

    const handleTimerExpiry = () => {
        if (testState === TestState.PREPARING) {
            playShortTone();
            startRecording();
        } else if (testState === TestState.RECORDING) {
            stopRecording();
        }
    };

    const playShortTone = () => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.frequency.value = 800;
            osc.start();
            osc.stop(audioContext.currentTime + 0.1);
        } catch (e) { console.error(e); }
    };

    const startRecording = () => {
        if (!stream) {
            setTestState(TestState.RECORDING);
            return;
        }

        setTestState(TestState.RECORDING);
        setTimeLeft(question.recordingTime || 40);
        chunksRef.current = [];
        setTranscription(''); // Reset transcription

        // Initialize Speech Recognition
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            console.log('[SpeechRecognition] Initialized');

            recognition.onstart = () => {
                console.log('[SpeechRecognition] Started');
            };

            recognition.onresult = (event: any) => {
                console.log('[SpeechRecognition] onresult fired, results:', event.results.length);
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    console.log(`[SpeechRecognition] Result ${i}: "${transcript}" (isFinal: ${event.results[i].isFinal})`);
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    }
                }
                if (finalTranscript) {
                    console.log('[SpeechRecognition] Final transcript:', finalTranscript);
                    setTranscription(prev => {
                        const updated = prev + finalTranscript;
                        console.log('[SpeechRecognition] Updated transcription state:', updated);
                        return updated;
                    });
                }
            };

            recognition.onerror = (event: any) => {
                console.error('[SpeechRecognition] Error:', event.error);
            };

            recognition.onend = () => {
                console.log('[SpeechRecognition] Ended');
            };

            try {
                recognition.start();
                recognitionRef.current = recognition;
            } catch (err) {
                console.error('[SpeechRecognition] Failed to start:', err);
            }
        } else {
            console.warn('[SpeechRecognition] API not available in this browser');
        }

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
            handleAnalyze(blob);
        };

        mediaRecorder.start();
    };

    const stopRecording = () => {
        // Stop speech recognition
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        } else {
            setTestState(TestState.COMPLETED);
        }
    };

    const handleAnalyze = async (blob: Blob) => {
        // Set to COMPLETED immediately so user can move to next question
        setTestState(TestState.COMPLETED);

        // Process analysis in background
        console.log('[handleAnalyze] Transcription to send:', transcription);
        console.log('[handleAnalyze] Transcription length:', transcription.length);
        try {
            const result = await scoreReadAloudAttempt(blob, question.questionContent, question._id, transcription);
            onResultChange(result);
        } catch (e) {
            console.error(e);
        }
    };

    const getStatusText = () => {
        if (testState === TestState.PREPARING) return "";
        if (testState === TestState.RECORDING) return `Recording... ${timeLeft} seconds remaining`;
        if (testState === TestState.ANALYZING) return "Processing...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "Ready";
    };

    const getTotalTime = () => testState === TestState.PREPARING ? (question.preparationTime || 40) : (question.recordingTime || 40);
    const progressPercentage = ((getTotalTime() - timeLeft) / getTotalTime()) * 100;

    return (
        <section className="passage-wrapper max-w-4xl mx-auto mt-0">
            <div className="passage-outer">
                <div className="passage-inner">
                    {/* Instructions at the Top */}
                    <div className="mb-6">
                        <h2 className="passage-title">Read Aloud</h2>
                        <p className="task-instruction text-gray-800 font-bold italic">
                            Look at the text below. Read this text aloud as naturally and clearly as possible. You have {question.recordingTime || 40} seconds to read aloud.
                        </p>
                    </div>

                    {/* Status Box in the Middle */}
                    <div className="max-w-md mx-auto mb-8">
                        <div className="status-box overflow-hidden bg-[#eef7fa] border border-[#d1d5db]">
                            <div className="answer-box-header bg-[#ddeef6] py-2 font-bold text-gray-700">
                                Recorded Answer
                            </div>
                            <div className="p-6 text-center space-y-4">


                                <div className="text-gray-700 text-left">
                                    {testState === TestState.PREPARING || testState === TestState.RECORDING ? (
                                        <div style={{ marginTop: '10px' }}>
                                            {/* Status Text for Preparing */}
                                            {testState === TestState.PREPARING && (
                                                <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                                    <span className="font-medium">Current Status:</span>{' '}
                                                    <span className="text-gray-600">Beginning in {timeLeft}s</span>
                                                </div>
                                            )}

                                            {/* Status Text for Recording */}
                                            {testState === TestState.RECORDING && (
                                                <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                                    <span className="font-medium">Recording</span>
                                                </div>
                                            )}

                                            {/* Progress Bar Container */}
                                            <div style={{
                                                width: '100%',
                                                height: '30px',
                                                backgroundColor: '#e5e7eb',
                                                borderRadius: '15px',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                border: '2px solid #9ca3af'
                                            }}>
                                                {/* Progress Bar Fill */}
                                                <div style={{
                                                    width: `${Math.max(progressPercentage, 3)}%`,
                                                    height: '100%',
                                                    backgroundColor: '#14b8a6',
                                                    transition: 'width 0.5s linear',
                                                    borderRadius: '15px'
                                                }}></div>

                                                {/* Audio Visualizer Overlay for Recording */}
                                                {testState === TestState.RECORDING && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        opacity: 0.7
                                                    }}>
                                                        <AudioVisualizer stream={stream} isRecording={true} />
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    ) : (
                                        <span className="text-gray-600">
                                            {testState === TestState.COMPLETED ? "Completed" : getStatusText()}
                                        </span>
                                    )}
                                </div>

                                {/* Old Recording Progress Bar - Now Removed */}
                            </div>
                        </div>
                    </div>

                    {/* Sentence/Text at the Bottom */}
                    <div className="pte-text-flow text-lg leading-relaxed text-gray-800">
                        {question.questionContent}
                    </div>
                </div>
            </div>
        </section>
    );
};

const RepeatSentenceTask: React.FC<TaskProps> = ({ question, savedResult, onResultChange }) => {
    const [testState, setTestState] = useState<TestState>(TestState.IDLE);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [countdown, setCountdown] = useState(3);
    const [recordTimeLeft, setRecordTimeLeft] = useState(15);
    const [volume, setVolume] = useState<number>(70);
    const [transcription, setTranscription] = useState('');

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        if (savedResult) {
            setTestState(TestState.COMPLETED);
        } else {
            startSequence();
        }
        return () => {
            window.speechSynthesis.cancel();
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [question._id, savedResult]);

    useEffect(() => {
        const initMic = async () => {
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setStream(audioStream);
            } catch (err) {
                console.error("Error accessing microphone:", err);
            }
        };
        initMic();
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    const startSequence = () => {
        setTestState(TestState.COUNTDOWN);
        setCountdown(3);
    };

    useEffect(() => {
        if (testState === TestState.COUNTDOWN) {
            if (countdown > 0) {
                const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                playAudio();
            }
        }
    }, [testState, countdown]);

    const playAudio = () => {
        setTestState(TestState.LISTENING);

        if (question.audio) {
            // Play Audio File
            const audio = new Audio(getAssetUrl(question.audio));
            audioRef.current = audio;

            audio.onended = () => {
                startRecording();
            };

            audio.onerror = (e) => {
                console.error("Audio playback error:", e);
                // Fallback to recording immediately if audio fails
                startRecording();
            };

            audio.play().catch(e => {
                console.error("Audio play failed:", e);
                // Try fallback to TTS if audio play fails (e.g. 404) or just start recording
                // For now, let's try TTS as backup
                playTTS();
            });
        } else {
            playTTS();
        }
    };

    const playTTS = () => {
        // Fallback to TTS using questionContent
        const textToSpeak = question.audioText || question.questionContent || "No content to play";
        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.9;
        utterance.lang = 'en-US';
        synthRef.current = utterance;

        utterance.onend = () => {
            startRecording();
        };

        utterance.onerror = (e) => {
            console.error("TTS error:", e);
            startRecording();
        };

        window.speechSynthesis.speak(utterance);
    };

    const startRecording = () => {
        if (!stream) {
            setTestState(TestState.RECORDING);
        } else {
            setTestState(TestState.RECORDING);
            setRecordTimeLeft(15);
            chunksRef.current = [];
            setTranscription('');

            // Initialize Speech Recognition
            const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            if (SpeechRecognition) {
                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = 'en-US';

                console.log('[SpeechRecognition Task2] Initialized');

                recognition.onstart = () => console.log('[SpeechRecognition Task2] Started');

                recognition.onresult = (event: any) => {
                    let finalTranscript = '';
                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        const transcript = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                            finalTranscript += transcript + ' ';
                        }
                    }
                    if (finalTranscript) {
                        console.log('[SpeechRecognition Task2] Final transcript:', finalTranscript);
                        setTranscription(prev => prev + finalTranscript);
                    }
                };

                recognition.onerror = (event: any) => console.error('[SpeechRecognition Task2] Error:', event.error);
                recognition.onend = () => console.log('[SpeechRecognition Task2] Ended');

                try {
                    recognition.start();
                    recognitionRef.current = recognition;
                } catch (err) {
                    console.error('[SpeechRecognition Task2] Failed to start:', err);
                }
            }

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
                handleAnalyze(blob);
            };

            mediaRecorder.start();
        }
    };

    useEffect(() => {
        if (testState === TestState.RECORDING) {
            if (recordTimeLeft > 0) {
                const timer = setTimeout(() => setRecordTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                stopRecording();
            }
        }
    }, [testState, recordTimeLeft]);

    const stopRecording = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        } else {
            setTestState(TestState.COMPLETED);
        }
    };

    const handleAnalyze = async (blob: Blob) => {
        // Set to COMPLETED immediately so user can move to next question
        setTestState(TestState.COMPLETED);

        // Process analysis in background
        console.log('[handleAnalyze Task2] Transcription length:', transcription.length);
        try {
            const result = await scoreRepeatSentenceAttempt(blob, question.questionContent, question._id, transcription);
            onResultChange(result);
        } catch (e) {
            console.error(e);
        }
    };

    const getStatusText = () => {
        if (testState === TestState.COUNTDOWN) return `Beginning in ${countdown} seconds`;
        if (testState === TestState.LISTENING) return "Playing";
        if (testState === TestState.RECORDING) return "Recording";
        if (testState === TestState.ANALYZING) return "Saving...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "Ready";
    };

    // Progress calculation for countdown/listening (total ~5 seconds: 3 countdown + ~2 listening)
    const getProgressPercentage = () => {
        if (testState === TestState.COUNTDOWN) {
            return ((3 - countdown) / 5) * 100; // 0%, 20%, 40%
        }
        if (testState === TestState.LISTENING) {
            return 60 + (40 * 0.5); // ~80% during listening
        }
        if (testState === TestState.RECORDING) {
            return ((15 - recordTimeLeft) / 15) * 100; // 0% to 100% during recording
        }
        return 0;
    };

    return (
        /* Unified Card - No outer/inner wrappers as requested - NOW WRAPPED */
        <section className="passage-wrapper max-w-4xl mx-auto mt-0">
            <div className="passage-outer">
                <div className="passage-inner">
                    <div className="status-box overflow-hidden max-w-md mx-auto mt-0 border border-[#d1d5db] rounded-lg bg-white shadow-sm">
                        {/* Unified Header with Instructions */}
                        <div className="bg-[#ddeef6] py-4 px-6 border-b border-[#cce4f0]">
                            <h2 className="passage-title">Repeat Sentence</h2>
                            <p className="task-instruction text-gray-800 font-bold italic">
                                You will hear a sentence. Please repeat the sentence exactly as you hear it. You will hear the sentence only once.
                            </p>
                        </div>

                        <div className="p-6 text-center space-y-6">
                            {/* Status and Volume Section */}
                            <div className="space-y-4 border-b border-gray-100 pb-4">
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-gray-700 font-medium">Status: </span>
                                    <span className="text-gray-900 font-semibold">{getStatusText()}</span>
                                </div>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', maxWidth: '300px', margin: '0 auto' }}>
                                    <VolumeDown sx={{ color: '#6b7280' }} />
                                    <Slider
                                        aria-label="Volume"
                                        value={volume}
                                        onChange={(event: Event, newValue: number | number[]) => setVolume(newValue as number)}
                                        sx={{
                                            color: '#14b8a6',
                                            '& .MuiSlider-thumb': {
                                                width: 12,
                                                height: 12,
                                            },
                                        }}
                                    />
                                    <VolumeUp sx={{ color: '#6b7280' }} />
                                </Box>

                                {/* Progress Bar - Audio Playback Progress */}
                                <div style={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    height: '30px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '15px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '2px solid #9ca3af',
                                    margin: '0 auto'
                                }}>
                                    {/* Progress Bar Fill */}
                                    {testState === TestState.LISTENING && (
                                        <div style={{
                                            width: `${getProgressPercentage()}%`,
                                            height: '100%',
                                            backgroundColor: '#14b8a6',
                                            transition: 'width 0.5s linear',
                                            borderRadius: '15px'
                                        }}></div>
                                    )}
                                    {testState === TestState.RECORDING && (
                                        <>
                                            <div style={{
                                                width: `${getProgressPercentage()}%`,
                                                height: '100%',
                                                backgroundColor: '#14b8a6',
                                                transition: 'width 0.5s linear',
                                                borderRadius: '15px'
                                            }}></div>
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                opacity: 0.7
                                            }}>
                                                <AudioVisualizer stream={stream} isRecording={true} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Recording Visualization Section */}
                            <div className="space-y-3">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Fixed Image Display Component to enforce strict sizing
const FixedImageDisplay: React.FC<{ src: string, alt: string }> = ({ src, alt }) => {
    return (
        <div
            className="bg-white p-4 rounded border border-gray-300 shadow-sm flex items-center justify-center"
            style={{
                height: '300px',
                width: '100%',
                overflow: 'hidden'
            }}
        >
            <img
                src={src}
                alt={alt}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }}
                onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/400x300?text=Image+Load+Error';
                }}
            />
        </div>
    );
};

const DescribeImageTask: React.FC<TaskProps> = ({ question, savedResult, onResultChange }) => {
    const [testState, setTestState] = useState<TestState>(TestState.PREPARING);
    const [timeLeft, setTimeLeft] = useState(question.preparationTime || 25);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [transcription, setTranscription] = useState('');

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        if (savedResult) {
            setTestState(TestState.COMPLETED);
        } else {
            setTestState(TestState.PREPARING);
            setTimeLeft(question.preparationTime || 25);
        }
    }, [question._id, savedResult]);

    useEffect(() => {
        const initMic = async () => {
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setStream(audioStream);
            } catch (err) {
                console.error("Error accessing microphone:", err);
            }
        };
        initMic();
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    useEffect(() => {
        if (testState === TestState.PREPARING) {
            if (timeLeft > 0) {
                const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                startRecording();
            }
        } else if (testState === TestState.RECORDING) {
            if (timeLeft > 0) {
                const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                stopRecording();
            }
        }
    }, [testState, timeLeft]);

    const startRecording = () => {
        setTestState(TestState.RECORDING);
        setTimeLeft(question.recordingTime || 40);
        chunksRef.current = [];
        setTranscription(''); // Reset transcription

        // Initialize Speech Recognition
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            console.log('[SpeechRecognition Task3] Initialized');

            recognition.onstart = () => {
                console.log('[SpeechRecognition Task3] Started');
            };

            recognition.onresult = (event: any) => {
                console.log('[SpeechRecognition Task3] onresult fired, results:', event.results.length);
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    console.log(`[SpeechRecognition Task3] Result ${i}: "${transcript}" (isFinal: ${event.results[i].isFinal})`);
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    }
                }
                if (finalTranscript) {
                    console.log('[SpeechRecognition Task3] Final transcript:', finalTranscript);
                    setTranscription(prev => prev + finalTranscript);
                }
            };

            recognition.onerror = (event: any) => {
                console.error('[SpeechRecognition Task3] Error:', event.error);
            };

            recognition.onend = () => {
                console.log('[SpeechRecognition Task3] Ended');
            };

            try {
                recognition.start();
                recognitionRef.current = recognition;
            } catch (err) {
                console.error('[SpeechRecognition Task3] Failed to start:', err);
            }
        }

        if (stream) {
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
                handleAnalyze(blob);
            };

            mediaRecorder.start();
        }
    };

    const stopRecording = () => {
        // Stop speech recognition
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        } else {
            setTestState(TestState.COMPLETED);
        }
    };

    const handleAnalyze = async (blob: Blob) => {
        setTestState(TestState.ANALYZING);
        console.log('[handleAnalyze Task3] Transcription length:', transcription.length);
        try {
            const result = await scoreDescribeImageAttempt(blob, question.questionContent, question._id, transcription);
            onResultChange(result);
        } catch (e) {
            console.error(e);
        }
        setTestState(TestState.COMPLETED);
    };

    const getTotalTime = () => testState === TestState.PREPARING ? (question.preparationTime || 25) : (question.recordingTime || 40);
    const progressPercentage = ((getTotalTime() - timeLeft) / getTotalTime()) * 100;

    return (
        <section className="passage-wrapper max-w-6xl mx-auto mt-6">
            <div className="passage-outer">
                <div className="passage-inner">
                    {/* Instructions at the Top */}
                    <div className="mb-6">
                        <h2 className="passage-title">Describe Image</h2>
                        <p className="task-instruction text-gray-800 font-bold italic">
                            Look at the diagram below. In {question.preparationTime || 25} seconds, please speak into the microphone and describe in detail what the diagram is showing. You will have {question.recordingTime || 40} seconds to give your response.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                        {/* Left Column: Image with Fixed Display */}
                        <div className="w-full">
                            <FixedImageDisplay
                                src={getAssetUrl(question.img || "") || 'https://placehold.co/400x300?text=No+Image'}
                                alt="Describe this"
                            />
                        </div>

                        {/* Right Column: Recording Box */}
                        <div className="h-fit w-full">
                            <div className="status-box overflow-hidden border border-[#d1d5db] rounded-lg bg-white shadow-sm">
                                <div className="answer-box-header bg-[#ddeef6] py-2 font-bold text-center text-gray-700">
                                    Recorded Answer
                                </div>
                                <div className="p-6 text-center space-y-4">
                                    <div className="text-gray-700 text-left">
                                        {testState === TestState.PREPARING || testState === TestState.RECORDING ? (
                                            <div style={{ marginTop: '10px' }}>
                                                {/* Status Text for Preparing */}
                                                {testState === TestState.PREPARING && (
                                                    <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                                        <span className="font-medium">Current Status:</span>{' '}
                                                        <span className="text-gray-600">Beginning in {timeLeft}s</span>
                                                    </div>
                                                )}

                                                {/* Status Text for Recording */}
                                                {testState === TestState.RECORDING && (
                                                    <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                                        <span className="font-medium">Recording</span>
                                                    </div>
                                                )}

                                                {/* Progress Bar Container */}
                                                <div style={{
                                                    width: '100%',
                                                    height: '30px',
                                                    backgroundColor: '#e5e7eb',
                                                    borderRadius: '15px',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    border: '2px solid #9ca3af'
                                                }}>
                                                    {/* Progress Bar Fill */}
                                                    <div style={{
                                                        width: `${Math.max(progressPercentage, 3)}%`,
                                                        height: '100%',
                                                        backgroundColor: '#14b8a6',
                                                        transition: 'width 0.5s linear',
                                                        borderRadius: '15px'
                                                    }}></div>

                                                    {/* Audio Visualizer Overlay for Recording */}
                                                    {testState === TestState.RECORDING && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            opacity: 0.7
                                                        }}>
                                                            <AudioVisualizer stream={stream} isRecording={true} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-600">
                                                {testState === TestState.COMPLETED ? "Completed" : "Ready"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const RespondToSituationTask: React.FC<TaskProps> = ({ question, savedResult, onResultChange }) => {
    const [testState, setTestState] = useState<TestState>(TestState.PREPARING);
    const [countdown, setCountdown] = useState(3);
    const [timeLeft, setTimeLeft] = useState(question.preparationTime || 20);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [transcription, setTranscription] = useState('');

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        if (savedResult) {
            setTestState(TestState.COMPLETED);
        } else {
            setTestState(TestState.COUNTDOWN);
            setCountdown(3);
        }
        return () => {
            window.speechSynthesis.cancel();
        }
    }, [question._id, savedResult]);

    useEffect(() => {
        const initMic = async () => {
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setStream(audioStream);
            } catch (err) {
                console.error("Error accessing microphone:", err);
            }
        };
        initMic();
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    useEffect(() => {
        if (testState === TestState.COUNTDOWN) {
            if (countdown > 0) {
                const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                playAudio();
            }
        }
    }, [testState, countdown]);

    const playAudio = () => {
        setTestState(TestState.LISTENING);
        const utterance = new SpeechSynthesisUtterance(question.questionContent);
        utterance.rate = 1.0;
        utterance.lang = 'en-US';
        synthRef.current = utterance;

        utterance.onend = () => {
            startPreparing();
        };

        window.speechSynthesis.speak(utterance);
    };

    const startPreparing = () => {
        setTestState(TestState.PREPARING);
        setTimeLeft(question.preparationTime || 20);
    };

    useEffect(() => {
        if (testState === TestState.PREPARING || testState === TestState.RECORDING) {
            if (timeLeft > 0) {
                const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                handleTimerExpiry();
            }
        }
    }, [testState, timeLeft]);

    const handleTimerExpiry = () => {
        if (testState === TestState.PREPARING) {
            playShortTone();
            startRecording();
        } else if (testState === TestState.RECORDING) {
            stopRecording();
        }
    };

    const playShortTone = () => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.frequency.value = 800;
            osc.start();
            osc.stop(audioContext.currentTime + 0.1);
        } catch (e) { console.error(e); }
    };

    const startRecording = () => {
        if (!stream) {
            setTestState(TestState.RECORDING);
            return;
        }

        setTestState(TestState.RECORDING);
        setTimeLeft(question.recordingTime || 40);
        chunksRef.current = [];
        setTranscription('');

        // Initialize Speech Recognition
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            console.log('[SpeechRecognition Task4] Initialized');

            recognition.onstart = () => console.log('[SpeechRecognition Task4] Started');

            recognition.onresult = (event: any) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    }
                }
                if (finalTranscript) {
                    console.log('[SpeechRecognition Task4] Final transcript:', finalTranscript);
                    setTranscription(prev => prev + finalTranscript);
                }
            };

            recognition.onerror = (event: any) => console.error('[SpeechRecognition Task4] Error:', event.error);
            recognition.onend = () => console.log('[SpeechRecognition Task4] Ended');

            try {
                recognition.start();
                recognitionRef.current = recognition;
            } catch (err) {
                console.error('[SpeechRecognition Task4] Failed to start:', err);
            }
        }

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
            handleAnalyze(blob);
        };

        mediaRecorder.start();
    };

    const stopRecording = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        } else {
            setTestState(TestState.COMPLETED);
        }
    };

    const handleAnalyze = async (blob: Blob) => {
        setTestState(TestState.ANALYZING);
        console.log('[handleAnalyze Task4] Transcription length:', transcription.length);
        try {
            const result = await scoreRespondToSituationAttempt(blob, question.questionContent, question._id, transcription);
            onResultChange(result);
        } catch (e) {
            console.error(e);
        }
        setTestState(TestState.COMPLETED);
    };

    const getTopStatusText = () => {
        if (testState === TestState.COUNTDOWN) return `Beginning in ${countdown} seconds.`;
        if (testState === TestState.LISTENING) return "Playing...";
        if (testState === TestState.PREPARING) return "Thinking...";
        if (testState === TestState.RECORDING) return "Recording...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "Ready";
    };

    const getBottomStatusText = () => {
        if (testState === TestState.PREPARING) return `Beginning in ${timeLeft} seconds.`;
        if (testState === TestState.RECORDING) return `Recording... ${timeLeft} seconds remaining.`;
        if (testState === TestState.ANALYZING) return "Processing...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "";
    };

    const getTotalTime = () => testState === TestState.PREPARING ? (question.preparationTime || 20) : (question.recordingTime || 40);
    const progressPercentage = ((getTotalTime() - timeLeft) / getTotalTime()) * 100;

    return (
        <section className="passage-wrapper max-w-4xl mx-auto mt-0">
            <div className="passage-outer">
                <div className="passage-inner">
                    <div className="mb-8">
                        <h2 className="passage-title text-center mb-4">Respond to a Situation</h2>
                        <p className="task-instruction mb-4">
                            Listen to and read a description of a situation. You will have {question.preparationTime} seconds to think about your answer. Then you will hear a beep. You will have {question.recordingTime} seconds to answer the question. Please answer as completely as you can.
                        </p>
                        <p className="text-gray-800 text-base leading-relaxed">
                            {question.questionContent}
                        </p>
                    </div>

                    <div className="max-w-md mx-auto space-y-6">
                        <div className="status-box p-6 text-center">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <span className="text-gray-700 font-medium">Status:</span>
                                <span className="text-gray-900">{getTopStatusText()}</span>
                            </div>

                            <div className="flex items-center gap-3">
                                <span className="text-gray-600 text-sm">Volume</span>
                                <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                                    <div className="absolute top-1/2 left-2/3 w-3 h-5 bg-gray-400 -translate-y-1/2 rounded-sm border border-gray-500 shadow-sm"></div>
                                    <div className="flex justify-between mt-3 px-1">
                                        {[...Array(10)].map((_, i) => (
                                            <div key={i} className="w-[1px] h-1 bg-gray-300"></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="status-box overflow-hidden h-fit">
                            <div className="answer-box-header">
                                Recorded Answer
                            </div>
                            <div className="p-8 text-center space-y-6">
                                <div className="text-gray-700 text-left min-h-[3rem]">
                                    <span className="font-medium text-sm">Current Status:</span> <br />
                                    <span className="text-gray-500 text-lg">{getBottomStatusText()}</span>
                                </div>

                                {/* Progress Bar Container */}
                                <div style={{
                                    width: '100%',
                                    height: '30px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '15px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '2px solid #9ca3af'
                                }}>
                                    {/* Progress Bar Fill */}
                                    <div style={{
                                        width: `${Math.max(progressPercentage, 3)}%`,
                                        height: '100%',
                                        backgroundColor: '#14b8a6',
                                        transition: 'width 0.5s linear',
                                        borderRadius: '15px'
                                    }}></div>

                                    {/* Audio Visualizer Overlay for Recording */}
                                    {testState === TestState.RECORDING && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            opacity: 0.7
                                        }}>
                                            <AudioVisualizer stream={stream} isRecording={true} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const AnswerShortQuestionTask: React.FC<TaskProps> = ({ question, savedResult, onResultChange }) => {
    const [testState, setTestState] = useState<TestState>(TestState.IDLE);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [countdown, setCountdown] = useState(3);
    const [recordTimeLeft, setRecordTimeLeft] = useState(10);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const recognitionRef = useRef<any>(null);
    const [transcription, setTranscription] = useState('');

    useEffect(() => {
        if (savedResult) {
            setTestState(TestState.COMPLETED);
        } else {
            startSequence();
        }
        return () => {
            window.speechSynthesis.cancel();
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [question._id, savedResult]);

    useEffect(() => {
        const initMic = async () => {
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setStream(audioStream);
            } catch (err) {
                console.error("Error accessing microphone:", err);
            }
        };
        initMic();
        return () => {
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    const startSequence = () => {
        setTestState(TestState.COUNTDOWN);
        setCountdown(3);
    };

    useEffect(() => {
        if (testState === TestState.COUNTDOWN) {
            if (countdown > 0) {
                const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                playAudio();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testState, countdown]);

    const playAudio = () => {
        setTestState(TestState.LISTENING);

        if (question.audio) {
            const audio = new Audio(getAssetUrl(question.audio));
            audioRef.current = audio;

            audio.onended = () => {
                startRecording();
            };

            audio.onerror = (e) => {
                console.error("Audio playback error:", e);
                playTTS();
            };

            audio.play().catch(e => {
                console.error("Audio play failed:", e);
                playTTS();
            });
        } else {
            playTTS();
        }
    };

    const playTTS = () => {
        const text = question.audioText || question.questionContent || "Listen to the question";
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.lang = 'en-US';
        synthRef.current = utterance;

        utterance.onend = () => {
            startRecording();
        };

        window.speechSynthesis.speak(utterance);
    };

    const startRecording = () => {
        if (!stream) {
            setTestState(TestState.RECORDING);
            return;
        }

        setTestState(TestState.RECORDING);
        setRecordTimeLeft(10);
        chunksRef.current = [];
        setTranscription(''); // Reset transcription

        // Initialize Speech Recognition
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event: any) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    }
                }
                if (finalTranscript) {
                    setTranscription(prev => prev + finalTranscript);
                }
            };

            recognition.onerror = (event: any) => {
                console.error('[SpeechRecognition] Error:', event.error);
            };

            try {
                recognition.start();
                recognitionRef.current = recognition;
            } catch (err) {
                console.error('[SpeechRecognition] Failed to start:', err);
            }
        }

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
            handleAnalyze(blob);
        };

        mediaRecorder.start();
    };

    useEffect(() => {
        if (testState === TestState.RECORDING) {
            if (recordTimeLeft > 0) {
                const timer = setTimeout(() => setRecordTimeLeft(t => t - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                stopRecording();
            }
        }
    }, [testState, recordTimeLeft]);

    const stopRecording = () => {
        // Stop speech recognition
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        } else {
            setTestState(TestState.COMPLETED);
        }
    };

    const handleAnalyze = async (blob: Blob) => {
        setTestState(TestState.ANALYZING);
        try {
            // Pass transcription to the scoring function
            const result = await scoreAnswerShortQuestionAttempt(blob, question.questionContent, question._id, transcription);
            onResultChange(result);
        } catch (e) {
            console.error(e);
        }
        setTestState(TestState.COMPLETED);
    };

    const getStatusText = () => {
        if (testState === TestState.COUNTDOWN) return `Beginning in ${countdown} seconds`;
        if (testState === TestState.LISTENING) return "Playing";
        if (testState === TestState.RECORDING) return "Recording";
        if (testState === TestState.ANALYZING) return "Saving...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "Ready";
    };

    const getBottomStatusText = () => {
        if (testState === TestState.COUNTDOWN) return `Beginning in ${countdown + 4} seconds`;
        if (testState === TestState.LISTENING) return "Preparing to record...";
        if (testState === TestState.RECORDING) return "Recording...";
        if (testState === TestState.ANALYZING) return "Processing...";
        if (testState === TestState.COMPLETED) return "Completed";
        return "";
    };

    const getProgressPercentage = () => {
        if (testState === TestState.RECORDING) {
            const total = question.recordingTime || 10;
            return ((total - recordTimeLeft) / total) * 100;
        }
        return 0;
    };

    return (
        <section className="passage-wrapper max-w-4xl mx-auto mt-0">
            <div className="passage-outer">
                <div className="passage-inner">
                    <div className="mb-8">
                        <h2 className="passage-title text-center mb-4">Answer Short Question</h2>
                        <p className="task-instruction text-center">
                            You will hear a question. Please give a simple and short answer. Often just one or a few words is enough.
                        </p>
                    </div>

                    <div className="space-y-6 max-w-md mx-auto">
                        <div className="status-box p-6 text-center">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <span className="text-gray-700 font-medium">Status:</span>
                                <span className="text-gray-900">{getStatusText()}</span>
                            </div>

                            <div className="flex items-center gap-3">
                                <span className="text-gray-600 text-sm">Volume</span>
                                <div className="flex-1 h-2 bg-gray-200 rounded-full relative">
                                    <div className="absolute top-1/2 left-2/3 w-3 h-5 bg-gray-400 -translate-y-1/2 rounded-sm border border-gray-500 shadow-sm cursor-pointer"></div>
                                    <div className="flex justify-between mt-3 px-1">
                                        {[...Array(10)].map((_, i) => (
                                            <div key={i} className="w-[1px] h-1 bg-gray-300"></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="status-box overflow-hidden">
                            <div className="answer-box-header">
                                Recorded Answer
                            </div>
                            <div className="p-6 text-center space-y-4">
                                <div className="text-gray-700">
                                    <span className="font-medium">Current Status:</span> <br />
                                    <span className="text-gray-500">{getBottomStatusText()}</span>
                                </div>

                                {/* Progress Bar Container */}
                                <div style={{
                                    width: '100%',
                                    height: '30px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '15px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '2px solid #9ca3af'
                                }}>
                                    {/* Progress Bar Fill */}
                                    <div style={{
                                        width: `${Math.max(getProgressPercentage(), 3)}%`,
                                        height: '100%',
                                        backgroundColor: '#14b8a6',
                                        transition: 'width 0.5s linear',
                                        borderRadius: '15px'
                                    }}></div>

                                    {/* Audio Visualizer Overlay for Recording */}
                                    {testState === TestState.RECORDING && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            opacity: 0.7
                                        }}>
                                            <AudioVisualizer stream={stream} isRecording={true} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- Main Component ---


// Interface for component props
interface PteCoreSpeakingTestProps {
    isFullTest?: boolean;
    onSectionComplete?: () => void;
}

export default function PteCoreSpeakingTest({ isFullTest = false, onSectionComplete }: PteCoreSpeakingTestProps) {
    const { testNumber } = useParams();
    const [questions, setQuestions] = useState<BaseQuestion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [results, setResults] = useState<Record<string, ScoreResult>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
    const timerRef = useRef<any | null>(null);

    useEffect(() => {
        const fetchQuestions = async () => {
            console.log("PTE SPEAKING: Fetching questions for testNumber:", testNumber);
            try {
                const res = await fetchPteCoreSpeakingQuestions(testNumber);
                console.log("PTE SPEAKING: API Response data type:", typeof res.data, "Length:", res.data?.length);
                console.log("PTE SPEAKING: API Response data:", res.data);

                if (typeof res.data === 'string' && res.data.includes('<!DOCTYPE html>')) {
                    console.error("PTE SPEAKING ERROR: Received HTML instead of JSON. Check backend routing.");
                }

                setQuestions(res.data);
            } catch (err) {
                console.error("PTE SPEAKING: Failed to fetch questions", err);
            } finally {
                setLoading(false);
            }
        };

        if (testNumber) {
            fetchQuestions();
        }
    }, [testNumber]);

    // Timer effect
    useEffect(() => {
        if (!questions.length || isSubmitted) return;
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setSecondsLeft(s => {
                if (s <= 1) {
                    clearInterval(timerRef.current!);
                    setIsSubmitted(true);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [questions, isSubmitted]);

    const formatTime = (secs: number) => {
        const mm = Math.floor(secs / 60).toString().padStart(2, "0");
        const ss = Math.floor(secs % 60).toString().padStart(2, "0");
        return `${mm}:${ss}`;
    };

    if (loading) {
        return <div className="container-centered"><div className="loading-msg">Loading speaking test...</div></div>;
    }

    if (!questions.length) {
        return <div className="container-centered"><div className="loading-msg">No questions found for this test number.</div></div>;
    }

    const currentQuestion = questions[currentIndex];

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            setIsSubmitted(true);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    const handleResultChange = (result: ScoreResult) => {
        setResults(prev => ({
            ...prev,
            [currentQuestion._id]: result
        }));
    };

    const renderTask = () => {
        const commonProps = {
            question: currentQuestion,
            savedResult: results[currentQuestion._id],
            onResultChange: handleResultChange
        };

        // Normalize task type check
        const title = currentQuestion.taskTitle?.toLowerCase() || currentQuestion.component?.toLowerCase() || "";

        if (title.includes('read aloud')) {
            return <ReadAloudTask {...commonProps} />;
        } else if (title.includes('repeat sentence')) {
            return <RepeatSentenceTask {...commonProps} />;
        } else if (title.includes('describe image')) {
            return <DescribeImageTask {...commonProps} />;
        } else if (title.includes('respond to a situation') || title.includes('respond to situation')) {
            return <RespondToSituationTask {...commonProps} />;
        } else if (title.includes('answer short question')) {
            return <AnswerShortQuestionTask {...commonProps} />;
        } else {
            return (
                <div className="p-8 text-center text-red-500">
                    Unknown question type: {currentQuestion.taskTitle || currentQuestion.component}
                </div>
            );
        }
    };

    // const currentResult = results[currentQuestion._id]; // Unused

    if (isSubmitted) {
        // Prepare results for all questions, handling unattempted ones
        // @ts-ignore
        const formattedResults: any[] = questions.map(q => {
            const existingResult = results[q._id];
            if (existingResult) {
                return {
                    taskId: q._id,
                    taskTitle: q.taskTitle || q.component || "Unknown Task",
                    transcription: existingResult.transcription,
                    score: existingResult
                };
            } else {
                // Return zero score for unattempted questions
                return {
                    taskId: q._id,
                    taskTitle: q.taskTitle || q.component || "Unknown Task",
                    transcription: "(Not attempted)",
                    score: {
                        content: 0,
                        pronunciation: 0,
                        fluency: 0,
                        overall: 0,
                        feedback: "This question was not attempted.",
                        transcription: ""
                    }
                };
            }
        });

        const handleComplete = () => {
            if (isFullTest && onSectionComplete) {
                onSectionComplete();
            } else {
                window.location.reload();
            }
        };

        return (
            <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-12">
                <header className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2">
                        <div className="bg-green-100 p-1 rounded-lg">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <span className="font-semibold text-gray-700">Test Submitted | Scores Ready</span>
                    </div>
                </header>

                <main className="max-w-4xl mx-auto px-4 py-8">
                    <PteCoreSpeakingScore
                        results={formattedResults}
                        onRetry={() => window.location.reload()}
                        onNext={isFullTest ? handleComplete : undefined}
                        nextLabel={isFullTest ? "Continue to Writing Section" : "Start New Test"}
                    />
                </main>
            </div>
        );
    }

    const answeredCount = Object.keys(results).length;

    return (
        <div className="ptest-root">
            {/* Header - matches reading test */}
            <header className="ptest-header" style={{ marginTop: '0px', marginBottom: '0px', paddingBottom: '0px' }}>
                <div className="ptest-title">
                    <h1>PTE Core Speaking Test {testNumber}</h1>
                    <div className="progress-indicator">
                        Question {currentIndex + 1} of {questions.length} • Answered {answeredCount}/{questions.length}
                    </div>
                </div>

                <div className="right-controls">
                    <div className={secondsLeft <= 5 * 60 ? "timer timer-warning" : "timer"}>
                        <span className="timer-emoji">⏱</span> {formatTime(secondsLeft)}
                    </div>
                </div>
            </header>

            {/* Main Content - matches reading test layout */}
            <main className="ptest-main--single-col" style={{ padding: '0px 12px 12px 12px' }}>
                {/* Task Title Section */}
                {/* Task Title Section - Hidden for Read Aloud as it has custom header */}
                {!(
                    currentQuestion.taskTitle?.toLowerCase().includes('read aloud') ||
                    currentQuestion.component?.toLowerCase().includes('read aloud') ||
                    currentQuestion.taskTitle?.toLowerCase().includes('describe image') ||
                    currentQuestion.component?.toLowerCase().includes('describe image') ||
                    currentQuestion.taskTitle?.toLowerCase().includes('repeat sentence') ||
                    currentQuestion.component?.toLowerCase().includes('repeat sentence') ||
                    currentQuestion.taskTitle?.toLowerCase().includes('respond to a situation') ||
                    currentQuestion.component?.toLowerCase().includes('respond to a situation') ||
                    currentQuestion.taskTitle?.toLowerCase().includes('respond to situation') ||
                    currentQuestion.component?.toLowerCase().includes('respond to situation') ||
                    currentQuestion.taskTitle?.toLowerCase().includes('answer short question') ||
                    currentQuestion.component?.toLowerCase().includes('answer short question')
                ) && (
                        <section className="passage-wrapper">
                            <div className="passage-outer">
                                <div className="passage-inner">
                                    <h2 className="passage-title" style={{ fontSize: '20px', marginBottom: '8px' }}>
                                        {currentQuestion.taskTitle || currentQuestion.component}
                                    </h2>
                                    <p style={{ color: '#666', marginBottom: '16px', fontStyle: 'italic' }}>
                                        {currentQuestion.questionTitle}
                                    </p>
                                </div>
                            </div>
                        </section>
                    )}

                {/* Question Content Area */}
                <section className="questions-wrapper" style={{ marginTop: '20px' }}>
                    {renderTask()}

                    {/* Navigation - matches reading test */}
                    <div className="ptest-header" style={{ marginTop: '20px' }}>
                        <div></div>
                        <div className="right-controls" style={{ display: 'flex', gap: '12px' }}>
                            <button
                                className="btn btn-ghost"
                                onClick={handlePrev}
                                disabled={currentIndex === 0}
                            >
                                ← Previous
                            </button>
                            {currentIndex === questions.length - 1 ? (
                                <button
                                    className="btn btn-submit"
                                    onClick={() => setIsSubmitted(true)}
                                >
                                    Submit Test
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleNext}
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}